import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Button, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select,
  MenuItem, Chip, Grid, Typography,
} from "@material-ui/core";
import { DeleteOutline, Edit, Search, Add, AssignmentOutlined } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const STATUS_OPTIONS = [
  { value: "pendente",     label: "Pendente",     color: "#f57c00" },
  { value: "em_andamento", label: "Em andamento",  color: "#1976d2" },
  { value: "concluido",    label: "Concluído",     color: "#388e3c" },
  { value: "cancelado",    label: "Cancelado",     color: "#d32f2f" },
];

const EMPTY_FORM = {
  title: "",
  description: "",
  taskDate: "",
  taskTime: "",
  status: "pendente",
  responsibleId: "",
};

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":   return action.payload;
    case "CREATE": return [action.payload, ...state];
    case "UPDATE": return state.map((t) => t.id === action.payload.id ? action.payload : t);
    case "DELETE": return state.filter((t) => t.id !== action.payload);
    default:       return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  chip: { fontWeight: 600, fontSize: 12 },
  filtersRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  emptyRow: { textAlign: "center", color: theme.palette.text.secondary },
}));

const Tasks = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";

  const [tasks, dispatch] = useReducer(reducer, []);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [searchParam, setSearchParam]   = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterResponsible, setFilterResponsible] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo]   = useState("");

  // Modal form
  const [modalOpen, setModalOpen]   = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // Confirm delete
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId]   = useState(null);

  // Carrega tarefas sempre que algum filtro muda
  useEffect(() => {
    setLoading(true);
    api
      .get("/tasks", {
        params: {
          searchParam:   searchParam   || undefined,
          status:        filterStatus  || undefined,
          responsibleId: filterResponsible || undefined,
          dateFrom:      filterDateFrom || undefined,
          dateTo:        filterDateTo   || undefined,
        },
      })
      .then(({ data }) => dispatch({ type: "LOAD", payload: data }))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [searchParam, filterStatus, filterResponsible, filterDateFrom, filterDateTo]);

  // Carrega usuários para selects (apenas admin precisa)
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/users", { params: { pageNumber: 1 } })
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => {});
  }, [isAdmin]);

  // Tempo real via Socket.IO
  useEffect(() => {
    const event = `company${user.companyId}-task`;
    const handler = (data) => {
      if (data.action === "create") dispatch({ type: "CREATE", payload: data.task });
      if (data.action === "update") dispatch({ type: "UPDATE", payload: data.task });
      if (data.action === "delete") dispatch({ type: "DELETE", payload: Number(data.taskId) });
    };
    socket.on(event, handler);
    return () => socket.off(event, handler);
  }, [socket, user.companyId]);

  const handleOpenNew = () => {
    setEditingTask(null);
    setForm({ ...EMPTY_FORM, responsibleId: isAdmin ? "" : user.id });
    setModalOpen(true);
  };

  const handleOpenEdit = (task) => {
    setEditingTask(task);
    setForm({
      title:         task.title || "",
      description:   task.description || "",
      taskDate:      task.taskDate || "",
      taskTime:      task.taskTime || "",
      status:        task.status || "pendente",
      responsibleId: task.responsibleId || "",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Título é obrigatório"); return; }
    try {
      const payload = { ...form, responsibleId: form.responsibleId || null };
      if (editingTask) {
        await api.put(`/tasks/${editingTask.id}`, payload);
        toast.success("Tarefa atualizada!");
      } else {
        await api.post("/tasks", payload);
        toast.success("Tarefa criada!");
      }
      setModalOpen(false);
    } catch (err) { toastError(err); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/tasks/${deletingId}`);
      toast.success("Tarefa deletada!");
    } catch (err) { toastError(err); }
    setDeletingId(null);
  };

  const clearFilters = () => {
    setSearchParam("");
    setFilterStatus("");
    setFilterResponsible("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const getStatusChip = (status) => {
    const opt = STATUS_OPTIONS.find((s) => s.value === status);
    if (!opt) return null;
    return (
      <Chip
        size="small"
        label={opt.label}
        className={classes.chip}
        style={{ backgroundColor: opt.color, color: "#fff" }}
      />
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const [y, m, d] = date.split("-");
    return `${d}/${m}/${y}`;
  };

  const hasFilters = filterStatus || filterResponsible || filterDateFrom || filterDateTo || searchParam;

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <AssignmentOutlined style={{ marginRight: 8, verticalAlign: "middle" }} />
          Tarefas
        </Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenNew}
            startIcon={<Add />}
          >
            Nova Tarefa
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Barra de filtros */}
      <Paper style={{ padding: "12px 16px", marginBottom: 8 }}>
        <div className={classes.filtersRow}>
          <TextField
            size="small"
            label="Buscar"
            placeholder="Título ou descrição..."
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            style={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: "gray", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" style={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filtro por responsável: só admin vê */}
          {isAdmin && (
            <FormControl size="small" style={{ minWidth: 160 }}>
              <InputLabel>Responsável</InputLabel>
              <Select value={filterResponsible} onChange={(e) => setFilterResponsible(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            size="small"
            label="Data de"
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ minWidth: 150 }}
          />

          <TextField
            size="small"
            label="Data até"
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ minWidth: 150 }}
          />

          {hasFilters && (
            <Button size="small" onClick={clearFilters} style={{ color: "#999" }}>
              Limpar filtros
            </Button>
          )}
        </div>
      </Paper>

      <Paper className={classes.mainPaper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Título</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Hora</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={7} />
            ) : tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className={classes.emptyRow}>
                  <Typography variant="body2" style={{ padding: 24 }}>
                    Nenhuma tarefa encontrada
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell><strong>{task.title}</strong></TableCell>
                  <TableCell style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {task.description || "-"}
                  </TableCell>
                  <TableCell>{formatDate(task.taskDate)}</TableCell>
                  <TableCell>{task.taskTime || "-"}</TableCell>
                  <TableCell>{task.responsible?.name || "-"}</TableCell>
                  <TableCell>{getStatusChip(task.status)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" title="Editar" onClick={() => handleOpenEdit(task)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Deletar"
                      onClick={() => { setDeletingId(task.id); setConfirmOpen(true); }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal criar/editar */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTask ? "Editar Tarefa" : "Nova Tarefa"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField
                label="Título *"
                fullWidth
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrição"
                fullWidth
                multiline
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Data da tarefa"
                type="date"
                fullWidth
                value={form.taskDate}
                onChange={(e) => setForm({ ...form, taskDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Hora"
                type="time"
                fullWidth
                value={form.taskTime}
                onChange={(e) => setForm({ ...form, taskTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Responsável</InputLabel>
                <Select
                  value={form.responsibleId}
                  onChange={(e) => setForm({ ...form, responsibleId: e.target.value })}
                  disabled={!isAdmin}
                >
                  {isAdmin && <MenuItem value="">— Nenhum —</MenuItem>}
                  {isAdmin
                    ? users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                      ))
                    : <MenuItem value={user.id}>{user.name}</MenuItem>
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editingTask ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Deletar Tarefa"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        Tem certeza que deseja deletar esta tarefa?
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Tasks;
